import { HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import {
  AfterSubmitProposalResponse,
  DocumentToUpload,
  FileToUpload,
  SubmitInfo,
  SubmitInfoMinor,
} from "@app/core/domain/submit-proposal";
import { ApiMappingService } from "@core/mapping/api-mapping.service";
import { GenericHttpService } from "@core/services/generic-http.service";
import { forkJoin, map, Observable, of } from "rxjs";
import { ApiAfterSubmitProposalResponse, ApiInfoVoorIndienen } from "../api/model/api-indienen-voorstel";
import { SubmitProposalMappingService } from "../mapping/submit-proposal-mapping.service";

@Injectable({
  providedIn: "root",
})
export class SubmitProposalService extends GenericHttpService {
  private readonly mappingService = inject(ApiMappingService);
  private readonly submitProposalMapper = inject(SubmitProposalMappingService);

  fetchSubmitInfo(planId: number): Observable<SubmitInfo | undefined> {
    const params = new HttpParams().set("spla_id", planId.toString());
    return this.httpGet<ApiInfoVoorIndienen>("plan/info_voor_indienen", params).pipe(
      map((apiSubmitInfo) => {
        return apiSubmitInfo ? this.mappingService.toSubmitInfo(apiSubmitInfo) : undefined;
      }),
    );
  }

  submitProposal(
    submitInfo: SubmitInfo,
    filesToUpload: FileToUpload[],
    explanation: string,
  ): Observable<AfterSubmitProposalResponse> {
    const proposal = this.submitProposalMapper.mapToApiIndienenVoorstelPayload(submitInfo, filesToUpload, explanation);
    const url = `plan/${submitInfo.planId}/indienen_plan_goedkeuring`;

    return this.httpPut<ApiAfterSubmitProposalResponse>(url, undefined, JSON.stringify(proposal)).pipe(
      map((apiAfterSubmitProposalResponse) =>
        this.mappingService.toAfterSubmitProposalResponse(apiAfterSubmitProposalResponse),
      ),
    );
  }

  uploadDocuments(
    afterSubmitProposalResponse: AfterSubmitProposalResponse,
    filesToUpload: FileToUpload[],
  ): Observable<void> {
    if (afterSubmitProposalResponse.documentsToUpload?.length) {
      this.addDocIdsToFilesToUpload(filesToUpload, afterSubmitProposalResponse.documentsToUpload);
      const uploadActions = filesToUpload.map((file) =>
        this.uploadDocument(afterSubmitProposalResponse.planId, file, afterSubmitProposalResponse.studentNr),
      );

      return forkJoin(uploadActions).pipe(
        map(() => {
          // console.log('All documents uploaded')
        }),
      );
    }

    // No documents to be uploaded
    return of(void 0);
  }

  private uploadDocument(planId: number, fileInfo: FileToUpload, studentNr: string): Observable<void> {
    const url = `plan/${planId}/indienen_plan_document/${fileInfo.documentId}/document`;

    return this.httpPut(url, undefined, fileInfo.file, this.getStudentNrHeader(studentNr));
  }

  private addDocIdsToFilesToUpload(filesToUpload: FileToUpload[], documentsToUpload: DocumentToUpload[]): void {
    documentsToUpload.forEach((documentToUpload) => {
      const fileToUpload = filesToUpload.find(
        (fileInfo) =>
          fileInfo.file.name === documentToUpload.documentName &&
          fileInfo.file.size === documentToUpload.documentSize &&
          fileInfo.documentId === undefined,
      );
      if (fileToUpload) {
        fileToUpload.documentId = documentToUpload.documentId;
      }
    });
  }

  minorEquals(minor1: SubmitInfoMinor, minor2: SubmitInfoMinor): boolean {
    return minor1.code === minor2.code && minor1.name === minor2.name;
  }
}
